import RouteTemplate from 'ember-route-template'
import Component from '@glimmer/component'
import { tracked } from '@glimmer/tracking'
import { assert } from '@ember/debug'
import { fn } from '@ember/helper'
import { on } from '@ember/modifier'
import { isPresent } from '@ember/utils'
import { t, type IntlService } from 'ember-intl'
import { service } from '@ember/service'
import { eq } from 'ember-truth-helpers'
import { orderBy } from 'lodash'
import type ManageRosterRoute from 'district-ui-client/subscription-type/manage/roster/route'
import type ExternalRosteringService from 'district-ui-client/services/external-rostering'
import { formatValue } from 'district-ui-client/utils/format-value'
import { ThemedLink } from 'district-ui-client/components/themed-button'
import { PhosphorIcon } from 'district-ui-client/components/phosphor-icon'
import { TableContainer, Table, TRHead, TBody, TH, TD, TFootSingleTd } from 'district-ui-client/components/table'
import { Panel, PanelBody } from 'district-ui-client/components/section'
import { ManageHeading } from 'district-ui-client/components/manage-heading'
import colspanMax from 'district-ui-client/modifiers/colspan-max'
import { BasePageSelector } from 'district-ui-client/components/base/page-selector'
import { PageSummary } from 'district-ui-client/components/base/page-summary'
import { PageSize } from 'district-ui-client/components/base/page-size'
import type { PageMeta } from 'district-ui-client/components/base/page-controls'
import type { PhosphorIconName } from 'phosphor-icons'

const DEFAULT_PAGE_SIZE = 25

interface RosterRow {
  schoolId: string
  schoolName: string
  schoolTown: string | ReturnType<typeof formatValue>
  schoolCode: string
  subscriptionCoordinators: string | ReturnType<typeof formatValue>
  teacherCount: number
  studentCount: number
  lastRolloverDate: Date | undefined
  lastRolloverFormatted: string | ReturnType<typeof formatValue>
}

type SortKey = keyof RosterRow
type SortDir = 'asc' | 'desc'

interface Signature {
  Args: {
    model: ModelFor<ManageRosterRoute>
  }
}

class ManageRosterTemplate extends Component<Signature> {
  @service intl!: IntlService
  @service externalRostering!: ExternalRosteringService

  @tracked currentPage = 1
  @tracked pageSize = DEFAULT_PAGE_SIZE

  @tracked sorts: `${SortKey}:${SortDir}`[] = ['schoolName:asc']

  get sortKey() {
    return this.sorts[0].split(':')[0] as SortKey
  }

  get sortDirection() {
    return this.sorts[0].split(':')[1] as SortDir
  }

  get sortIcon(): PhosphorIconName {
    switch (this.sortDirection) {
      case 'asc':
        return 'phosphor-caret-up-fill'
      case 'desc':
        return 'phosphor-caret-down-fill'
      default:
        return 'phosphor-caret-up-down-fill'
    }
  }

  get rows(): RosterRow[] {
    const { subscriptionType, schoolRollovers } = this.args.model
    return this.args.model.schools
      .map((school) => {
        const schoolStat = this.args.model.schoolStats.find((stat) => stat.id === school.id)

        const subcoNames = school.subscriptionCoordinators
          ?.filter((subco) => subco.isForSubscriptionType(subscriptionType))
          .map((subco) => subco.teacher?.fullName)
          .filter(isPresent)
          .join(', ')

        const rolloversForSchool = schoolRollovers.filter(
          (r) => r.school?.id === school.id && r.subscriptionType === subscriptionType && r.createdAt,
        )
        const lastRolloverDate = rolloversForSchool.length
          ? new Date(Math.max(...rolloversForSchool.map((r) => r.createdAt!.getTime())))
          : undefined

        return {
          schoolId: school.id,
          schoolName: school.name,
          schoolTown: formatValue(school.contactDetail?.town),
          schoolCode: school.schoolCode,
          subscriptionCoordinators: formatValue(subcoNames || undefined),
          teacherCount: schoolStat?.teacherCountFor(subscriptionType) ?? 0,
          studentCount: schoolStat?.studentCountFor(subscriptionType) ?? 0,
          lastRolloverDate,
          lastRolloverFormatted: lastRolloverDate
            ? this.intl.formatDate(lastRolloverDate, { day: '2-digit', month: 'short', year: 'numeric' })
            : formatValue(undefined),
        }
      })
      .filter(isPresent)
  }

  get sortedRows() {
    const properties = this.sorts.map((sort) => sort.split(':')[0])
    const directions = this.sorts.map((sort) => sort.split(':')[1])
    assert(
      'unknown sort direction',
      directions.every((d) => d === 'asc' || d === 'desc'),
    )
    return orderBy(this.rows, properties, directions)
  }

  get totalEntries() {
    return this.sortedRows.length
  }

  get totalPages() {
    return Math.ceil(this.totalEntries / this.pageSize) || 1
  }

  get pageMeta(): PageMeta {
    return {
      page_number: this.currentPage,
      page_size: this.pageSize,
      total_entries: this.totalEntries,
      total_pages: this.totalPages,
    }
  }

  get paginatedContent() {
    const start = (this.currentPage - 1) * this.pageSize
    return this.sortedRows.slice(start, start + this.pageSize)
  }

  changePage = (page: number) => {
    this.currentPage = page
  }

  changePageSize = (size: number) => {
    this.pageSize = size
    this.currentPage = 1
  }

  rosterUrlForSchool = (schoolId: string): string => {
    return this.externalRostering.oneRosterUrl(schoolId, 'manage-classes', this.args.model.subscriptionType)
  }

  sortBy = (property: SortKey) => {
    const [sortProperty, sortDirection] = this.sorts[0].split(':') as [SortKey, SortDir]
    if (sortProperty === property) {
      this.sorts = [`${property}:${sortDirection === 'asc' ? 'desc' : 'asc'}`]
    } else {
      this.sorts = [`${property}:asc`]
    }
    this.currentPage = 1
  }

  <template>
    <ManageHeading>{{t "subscriptionType.manageRoster.index.manageRoster"}}</ManageHeading>

    <div class="mt-8 space-y-6">
      <Panel>
        <PanelBody class="!pt-0" @tight={{true}}>
          <TableContainer>
            <Table @minWidth="min-w-[68rem]">
              <thead>
                <TRHead>
                  <TH @clickable={{true}} {{on "click" (fn this.sortBy "schoolName")}} data-test-roster-school-name-col>
                    {{t "subscriptionType.manageRoster.index.name"}}
                    <PhosphorIcon
                      @icon={{if (eq this.sortKey "schoolName") this.sortIcon "phosphor-caret-up-down-fill"}}
                    />
                  </TH>
                  <TH @clickable={{true}} {{on "click" (fn this.sortBy "schoolTown")}}>
                    {{t "subscriptionType.manageRoster.index.city"}}
                    <PhosphorIcon
                      @icon={{if (eq this.sortKey "schoolTown") this.sortIcon "phosphor-caret-up-down-fill"}}
                    />
                  </TH>
                  <TH @clickable={{true}} {{on "click" (fn this.sortBy "schoolCode")}} class="w-32">
                    {{t "subscriptionType.manageRoster.index.code"}}
                    <PhosphorIcon
                      @icon={{if (eq this.sortKey "schoolCode") this.sortIcon "phosphor-caret-up-down-fill"}}
                    />
                  </TH>
                  <TH @clickable={{true}} {{on "click" (fn this.sortBy "subscriptionCoordinators")}} class="w-48">
                    {{t "subscriptionType.manageRoster.index.subscriptionCoordinator" htmlSafe=true}}
                    <PhosphorIcon
                      @icon={{if
                        (eq this.sortKey "subscriptionCoordinators")
                        this.sortIcon
                        "phosphor-caret-up-down-fill"
                      }}
                    />
                  </TH>
                  <TH @clickable={{true}} {{on "click" (fn this.sortBy "teacherCount")}} class="w-36">
                    {{t "subscriptionType.manageRoster.index.teachers" htmlSafe=true}}
                    <PhosphorIcon
                      @icon={{if (eq this.sortKey "teacherCount") this.sortIcon "phosphor-caret-up-down-fill"}}
                    />
                  </TH>
                  <TH @clickable={{true}} {{on "click" (fn this.sortBy "studentCount")}} class="w-36">
                    {{t "subscriptionType.manageRoster.index.students" htmlSafe=true}}
                    <PhosphorIcon
                      @icon={{if (eq this.sortKey "studentCount") this.sortIcon "phosphor-caret-up-down-fill"}}
                    />
                  </TH>
                  <TH @clickable={{true}} {{on "click" (fn this.sortBy "lastRolloverDate")}} class="w-36">
                    {{t "subscriptionType.manageRoster.index.lastRolloverDate" htmlSafe=true}}
                    <PhosphorIcon
                      @icon={{if (eq this.sortKey "lastRolloverDate") this.sortIcon "phosphor-caret-up-down-fill"}}
                    />
                  </TH>
                  <TH class="w-28" />
                </TRHead>
              </thead>
              <TBody>
                {{#each this.paginatedContent as |row index|}}
                  <tr class="hover:bg-blue-50" data-test-roster-row={{index}}>
                    <TD data-test-roster-school-name>{{row.schoolName}}</TD>
                    <TD data-test-roster-city>{{row.schoolTown}}</TD>
                    <TD data-test-roster-code>{{row.schoolCode}}</TD>
                    <TD data-test-roster-subco>{{row.subscriptionCoordinators}}</TD>
                    <TD data-test-roster-teachers>{{row.teacherCount}}</TD>
                    <TD data-test-roster-students>{{row.studentCount}}</TD>
                    <TD data-test-roster-last-rollover>{{row.lastRolloverFormatted}}</TD>
                    <TD data-test-roster-button>
                      <ThemedLink
                        class="flex w-full items-center justify-center gap-1"
                        @style="neutral"
                        href={{this.rosterUrlForSchool row.schoolId}}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {{t "subscriptionType.manageRoster.index.roster"}}
                        <PhosphorIcon @icon="phosphor-arrow-square-out-bold" class="shrink-0" />
                      </ThemedLink>
                    </TD>
                  </tr>
                {{else}}
                  <tr>
                    <TD {{colspanMax}}>
                      {{t "noResults"}}
                    </TD>
                  </tr>
                {{/each}}
              </TBody>
              {{#if this.totalEntries}}
                <TFootSingleTd>
                  <div class="px-6 pb-6 pt-4">
                    <div class="flex items-center justify-between">
                      <PageSummary @meta={{this.pageMeta}} />
                      <PageSize @value={{this.pageSize}} @onChange={{this.changePageSize}} />
                    </div>
                    <BasePageSelector
                      @pageCount={{this.totalPages}}
                      @currentPage={{this.currentPage}}
                      @changePage={{this.changePage}}
                    />
                  </div>
                </TFootSingleTd>
              {{/if}}
            </Table>
          </TableContainer>
        </PanelBody>
      </Panel>
    </div>
  </template>
}

export default RouteTemplate(ManageRosterTemplate)
